'use client'

import { useEffect, useRef } from 'react'
import { captureEvent, EVENTS } from '@/lib/posthog/events'

interface Props {
  code: string
  sourcePage: 'pricing' | 'signup'
}

/**
 * Fires `referral_link_visited` exactly once per mount. Rendered by server components
 * ONLY when `?ref=CODE` resolves to a valid row in `referrers` (invalid / share-prefixed /
 * typo codes never get here, so the funnel denominator stays clean).
 *
 * Writes `$set_once: { first_referral_code_seen }` so true first-touch is locked in
 * before signup — without this write, a user who visits `?ref=jack` then `?ref=alice`
 * before signing up would have alice attributed as their first-touch on signup_completed.
 * With person_profiles: 'identified_only', the anonymous person profile is created on
 * first identify() and PostHog's identity-merge carries $set_once writes across.
 */
export function TrackReferralVisit({ code, sourcePage }: Props) {
  const firedRef = useRef(false)
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    captureEvent(EVENTS.REFERRAL_LINK_VISITED, {
      referral_code: code,
      source_page: sourcePage,
      $set_once: { first_referral_code_seen: code },
    })
  }, [code, sourcePage])
  return null
}
